/**
 * Validates uniqueness of user account, email, and aliases across the enterprise.
 * Prevents conflicts with other employees, self-duplication with own account, and group mail collisions.
 *
 * @param {string|null} currentUserId - ID of the user being edited (or null if creating new user)
 * @param {string} account - Target account/ID
 * @param {string} email - Target primary email
 * @param {string|string[]} aliases - Target aliases (string comma-separated or array)
 * @param {Array} allUsers - List of all existing users
 * @param {Array} groupMails - List of all group mails (e.g. all@clouflake.com)
 * @returns {{ isValid: boolean, error?: string, cleanedAliases: string[], cleanedAliasesStr: string, selfFilteredNotice?: string }}
 */
export const validateUserIdentityUniqueness = (
  currentUserId,
  account,
  email,
  aliases,
  allUsers = [],
  groupMails = []
) => {
  const normAcc = (account || '').trim().toLowerCase().replace('@clouflake.com', '');
  const normEmail = (email || `${normAcc}@clouflake.com`).trim().toLowerCase();

  // 1. Group emails to block
  const blockedGroupEmails = new Set([
    'all@clouflake.com',
    'all',
    ...(groupMails || []).map(g => (g.GroupEmail || '').trim().toLowerCase().replace('@clouflake.com', '')),
    ...(groupMails || []).map(g => (g.GroupEmail || '').trim().toLowerCase())
  ]);

  if (normAcc && (blockedGroupEmails.has(normAcc) || blockedGroupEmails.has(`${normAcc}@clouflake.com`))) {
    return {
      isValid: false,
      error: `'${normAcc}'은(는) 사내 공용 그룹 메일 주소이므로 개인 아이디로 사용할 수 없습니다.`
    };
  }

  // Parse and normalize target aliases
  const rawAliasTokens = (Array.isArray(aliases) ? aliases : (aliases || '').split(','))
    .map(a => a.trim().toLowerCase())
    .filter(Boolean);

  let selfFilteredCount = 0;
  const normalizedTargetAliases = [];

  for (const rawToken of rawAliasTokens) {
    const cleanToken = rawToken.replace('@clouflake.com', '').trim();
    if (!cleanToken) continue;
    const fullEmail = `${cleanToken}@clouflake.com`;

    // Rule 1: Self-duplication check (Do not allow own account or own primary email as alias)
    if (cleanToken === normAcc || fullEmail === normEmail) {
      selfFilteredCount++;
      continue;
    }

    // Rule 2: Group email check
    if (blockedGroupEmails.has(cleanToken) || blockedGroupEmails.has(fullEmail)) {
      return {
        isValid: false,
        error: `'${cleanToken}'은(는) 사내 공용 그룹 메일 주소이므로 별칭으로 등록할 수 없습니다.`
      };
    }

    if (!normalizedTargetAliases.includes(fullEmail)) {
      normalizedTargetAliases.push(fullEmail);
    }
  }

  // Check conflicts with OTHER users
  const otherUsers = (allUsers || []).filter(u => u.UserID !== currentUserId && u.UserID !== 'system-config');

  for (const user of otherUsers) {
    const otherAcc = (user.Account || '').trim().toLowerCase().replace('@clouflake.com', '');
    const otherEmail = (user.Email || '').trim().toLowerCase();
    const otherEmailPrefix = otherEmail.replace('@clouflake.com', '');
    const otherAliases = (user.Aliases || '')
      .split(',')
      .map(a => a.trim().toLowerCase().replace('@clouflake.com', ''))
      .filter(Boolean);

    const otherAllIdents = new Set([otherAcc, otherEmailPrefix, ...otherAliases]);

    // Check account conflict with other user's account, email or aliases
    if (normAcc && otherAllIdents.has(normAcc)) {
      const conflictType = otherAliases.includes(normAcc) ? '별칭(Alias)' : '기본 계정';
      return {
        isValid: false,
        error: `아이디 '${normAcc}'은(는) 이미 '${user.Name}' 직원이 ${conflictType}으로 사용 중입니다.`
      };
    }

    // Check alias conflicts with other user's account, email or aliases
    for (const targetAlias of normalizedTargetAliases) {
      const aliasPrefix = targetAlias.replace('@clouflake.com', '');
      if (otherAllIdents.has(aliasPrefix)) {
        const conflictType = otherAliases.includes(aliasPrefix) ? '별칭(Alias)' : '기본 계정';
        return {
          isValid: false,
          error: `별칭 '${aliasPrefix}'은(는) 이미 '${user.Name}' 직원이 ${conflictType}으로 사용 중입니다.`
        };
      }
    }
  }

  let selfFilteredNotice = '';
  if (selfFilteredCount > 0) {
    selfFilteredNotice = '본인의 기본 계정 아이디와 동일한 별칭은 불필요하므로 자동으로 제외되었습니다.';
  }

  return {
    isValid: true,
    cleanedAliases: normalizedTargetAliases,
    cleanedAliasesStr: normalizedTargetAliases.join(', '),
    selfFilteredNotice
  };
};
